import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'
import ImageTop1 from './images/top.webp'
import TabButtons from '@/components/TabButtons';
import Rewards from './components/Rewards';
import Rule from './components/Rule';
import Rank from './components/Rank';
import { getRank, getRewardsPool } from './services'
import { toast } from '@/utils/bridge'
import { useDesign } from '@/hooks'
import Loading from '@/components/Loading'
import Star from './components/Star'
import PaneTitle from './components/PaneTitle'
import Gift from './components/Gift'
import './index.scss'

const { prefixCls } = useDesign('pg-week-cp')

function WeekCP() {
    const { t, i18n } = useTranslation()
    const [loading, setLoading] = useState(false)
    const [state, setState] = useState<{
        selectedTab: number
        rankList: {
            thisWeek: WeekCP.RankItem[]
            lastWeek: WeekCP.RankItem[]
        }
        rewardsPool?: WeekCP.RewardsPool
    }>({
        selectedTab: 0,
        rankList: {
            thisWeek: [],
            lastWeek: []
        }
    })
    const refState = useRef(state)
    refState.current = state
    const rankList = useMemo(() => state.selectedTab === 0 ? state.rankList.thisWeek : state.rankList.lastWeek, [JSON.stringify(state)])

    const tabsTitles = useMemo(() => {
        return ['WeekCP.ThisWeek', 'WeekCP.LastWeek', 'WeekCP.RewardRule']
    }, [])

    const seconds = useMemo(() => {
        return state.rewardsPool?.endTime ? Number(((state.rewardsPool.endTime - Date.now()) / 1000).toFixed(0)) : 0
    }, [state.rewardsPool?.endTime])

    const fetchGetRewardsPool = useCallback(async () => {
        const res = await getRewardsPool()
        if (!res) return
        if (res.code !== 200) {
            toast(res.message)
            return
        }

        setState({
            ...refState.current,
            rewardsPool: res.data || {},
        })
    }, [])

    const fetchRankList = useCallback(async () => {
        setLoading(true)
        const [resThis, resLast] = await Promise.all([
            getRank(1),
            getRank(0)
        ])
        setLoading(false)

        if (!resThis || !resLast) return

        if (resThis.code !== 200) {
            toast(resThis.message)
            return
        }

        if (resLast.code !== 200) {
            toast(resLast.message)
            return
        }

        setState({
            ...refState.current,
            rankList: {
                thisWeek: resThis.data.rankList,
                lastWeek: resLast.data.rankList
            }
        })
    }, [])

    const handleTabChange = useCallback((i: number) => {
        const data = i == 1 ? refState.current.rankList.lastWeek : refState.current.rankList.thisWeek
        setState({
            ...refState.current,
            selectedTab: i
        })
    }, [])

    useEffect(() => {
        fetchRankList()
        fetchGetRewardsPool()
    }, [])

    return (
        <div className={clsx(prefixCls)}>
            {loading && <Loading />}
            <div className={`${prefixCls}__stars`}>
                {rankList[0] && [{
                    avatar: rankList[0].avatar
                }, {
                    avatar: rankList[0].cpAvatar
                }].map((star, i) => <Star avatar={star.avatar} key={i} />)}
                {rankList[0] && <Gift big={true} pic={rankList[0].cpMemento.picUrl} level={rankList[0].cpLevel} />}
                <img className="top1" src={ImageTop1} />
            </div>
            <TabButtons selectedTab={state.selectedTab} setSelectedTab={handleTabChange} tabText={tabsTitles}>
                {state.selectedTab === 0 &&
                    <div className={`${prefixCls}__container`}>
                        <PaneTitle title={t('WeekCP.CurrentRank')} />
                        <Rank
                            title="Current Rank"
                            selectedTab={state.selectedTab}
                            seconds={seconds}
                            rankList={state.rankList.thisWeek} />
                    </div>
                }
                {state.selectedTab === 1 &&
                    <div className={`${prefixCls}__container`}>
                        <PaneTitle title={t('WeekCP.LastWeekRank')} />
                        <div className='padding' />
                        <Rank
                            title="Rank Rewards"
                            selectedTab={state.selectedTab}
                            rankList={state.rankList.lastWeek} />
                    </div>
                }
                {state.selectedTab === 2 &&
                    <div >
                        <div className={clsx(`${prefixCls}__container`)}>
                            <PaneTitle title={t('WeekCP.RankRewards')} />
                            <Rewards />
                        </div>
                        {state.rewardsPool && <div className={clsx(`${prefixCls}__container`)}>
                            <PaneTitle title={t('Common.ActiveRule')} />
                            <Rule activityThresholdValue={state.rewardsPool.activityThresholdValue} />
                        </div>}
                    </div>
                }
            </TabButtons>
        </div >
    )
}
export default WeekCP